import { memo, useCallback, useMemo } from 'react';

import {
  ChakraSimpleGrid,
  DatePickerInput,
  FAIconName,
  InputGroupV2,
  TextInputV2,
  UniversalSelect,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { CoverageInfoValue } from '~/components/common/inquiry/drawer/types';
import {
  OTHER_PAYOR,
  TextInputV2Type,
} from '~/lib/constants/inquiry.constants';
import { patientCoveragePriorityOptionsFullList } from '~/lib/util/patientCoverage.util';

import InquiryDrawerLayout from './InquiryDrawerLayout';
import StateDropdown from './StateDropdown';
import {
  buildUniversalSelectSearchTerm,
  getSelectedUniversalSelectOption,
} from '~/lib/util/inquiry.util';
import {
  useGetCoverageSubscriberRelationsToPatientQuery,
  useGetPayorsEnumQuery,
} from '~/store/services/enumerations';
import { isEmpty } from 'lodash';

const TOP_LEVEL_ID = 'coverage-info';

const SELF = 'Self';

const isSubscriberSelf = (relationToPatient?: string): boolean =>
  isEmpty(relationToPatient) || relationToPatient === SELF;

interface CoverageInfoProps {
  coverageInfoValue: CoverageInfoValue;
  showRequiredFieldErrors: boolean;
  updateCoverageInfo: (info: Partial<CoverageInfoValue>) => void;
}

const CoverageInfo = memo(
  ({
    coverageInfoValue,
    showRequiredFieldErrors,
    updateCoverageInfo,
  }: CoverageInfoProps) => {
    const { data: patientRelationsToSubscriber } =
      useGetCoverageSubscriberRelationsToPatientQuery();

    const { data: payors } = useGetPayorsEnumQuery();

    const {
      alternativeId,
      coveragePriority,
      customPayorName,
      endDate,
      groupId,
      patientRelationToSubscriber,
      payor,
      providerServicesPhoneNumber,
      stateCode,
      startDate,
      subscriberId,
      subscriberFirstName,
      subscriberLastName,
      subscriberDateOfBirth,
    } = coverageInfoValue;
    const invalidPayor = showRequiredFieldErrors && !payor;
    const isMissingRequired = invalidPayor;
    const isCustomPayor = payor === OTHER_PAYOR;
    const isInvalidCustomPayor =
      showRequiredFieldErrors && !(isCustomPayor && customPayorName);
    const shouldShowSubscriberInput = !isSubscriberSelf(
      patientRelationToSubscriber,
    );

    const patientRelationToSubscriberOptions = useMemo<
      UniversalSelectOption[]
    >(() => {
      return (patientRelationsToSubscriber ?? []).map(({ label }) => ({
        label,
        searchData: buildUniversalSelectSearchTerm(label),
        value: label,
      }));
    }, [patientRelationsToSubscriber]);
    const priorityOptions = useMemo<UniversalSelectOption[]>(() => {
      return (patientCoveragePriorityOptionsFullList ?? []).map(
        ({ node, value }) => ({
          label: node,
          searchData: buildUniversalSelectSearchTerm(node),
          value,
        }),
      );
    }, []);

    const updateStringValue = useCallback(
      (key: keyof CoverageInfoValue) =>
        (val: string): void => {
          updateCoverageInfo({ [key]: val });
        },
      [],
    );

    const updateRelationToSubscriber = (relation?: string) => {
      const baseUpdate: Partial<CoverageInfoValue> = isSubscriberSelf(relation)
        ? {
            subscriberDateOfBirth: null,
            subscriberFirstName: null,
            subscriberLastName: null,
          }
        : {};
      const update: Partial<CoverageInfoValue> = {
        ...baseUpdate,
        patientRelationToSubscriber: relation,
      };

      updateCoverageInfo(update);
    };

    const payorRenderer = useMemo(() => {
      const activePayorMenuOptions: UniversalSelectOption[] = (payors ?? [])
        .filter((i) => i.isActive)
        .map(({ label }) => ({
          label,
          searchData: buildUniversalSelectSearchTerm(label),
          value: label,
        }));
      return (
        <ChakraSimpleGrid columns={1} spacing="24px" width="100%">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__insurance-provider`}
            label="Insurance provider"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__payor`}
              isInvalid={invalidPayor}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updateCoverageInfo({
                  payor: selected?.value,
                })
              }
              options={activePayorMenuOptions}
              value={getSelectedUniversalSelectOption(
                payor,
                activePayorMenuOptions,
              )}
            />
          </InputGroupV2>
          {isCustomPayor && (
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__custom-insurance-provider`}
              isRequired={isCustomPayor}
              label="Please enter your insurance provider"
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__custom-insurance-provider`}
                isInvalid={isInvalidCustomPayor}
                onChange={updateStringValue('customPayorName')}
                value={customPayorName}
              />
            </InputGroupV2>
          )}
        </ChakraSimpleGrid>
      );
    }, [showRequiredFieldErrors, payor, payors, customPayorName]);

    return (
      <InquiryDrawerLayout
        header="Coverage information"
        id={TOP_LEVEL_ID}
        isMissingFields={isMissingRequired}
      >
        {payorRenderer}

        <ChakraSimpleGrid columns={2} spacing="24px" width="100%">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__member-id`}
            isRequired={false}
            label="Member ID"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__member-id`}
              onChange={updateStringValue('subscriberId')}
              value={subscriberId}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__group-id`}
            isRequired={false}
            label="Group ID"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__group-id`}
              onChange={updateStringValue('groupId')}
              value={groupId}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__alternative-id`}
            isRequired={false}
            label="Alternative ID"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__alternative-id`}
              onChange={updateStringValue('alternativeId')}
              value={alternativeId}
            />
          </InputGroupV2>
          <StateDropdown
            id={TOP_LEVEL_ID}
            label="State"
            onSelect={updateStringValue('stateCode')}
            value={stateCode}
          />
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__patient-relationship-to-subscriber`}
            isRequired={false}
            label="Patient relationship to subscriber"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__patient-relationship-to-subscriber`}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updateRelationToSubscriber(selected?.value)
              }
              options={patientRelationToSubscriberOptions}
              value={getSelectedUniversalSelectOption(
                patientRelationToSubscriber,
                patientRelationToSubscriberOptions,
              )}
            />
          </InputGroupV2>
          {shouldShowSubscriberInput && (
            <>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__custom-insurance-subscriber-first-name`}
                isRequired={false}
                label="Subscriber first name"
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__custom-insurance-subscriber-first-name`}
                  isInvalid={isInvalidCustomPayor}
                  onChange={updateStringValue('subscriberFirstName')}
                  value={subscriberFirstName}
                />
              </InputGroupV2>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__custom-insurance-subscriber-last-name`}
                isRequired={false}
                label="Subscriber last name"
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__custom-insurance-subscriber-last-name`}
                  isInvalid={isInvalidCustomPayor}
                  onChange={updateStringValue('subscriberLastName')}
                  value={subscriberLastName}
                />
              </InputGroupV2>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__custom-insurance-subscriber-dob`}
                isRequired={false}
                label="Subscriber date of birth"
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__custom-insurance-subscriber-dob`}
                  onChange={(value: string) => {
                    updateCoverageInfo({
                      subscriberDateOfBirth: value,
                    });
                  }}
                  placeholder="MM/DD/YYYY"
                  type={TextInputV2Type.DATE}
                  value={subscriberDateOfBirth}
                />
              </InputGroupV2>
            </>
          )}
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__priority`}
            isRequired={false}
            label="Priority"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__priority`}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updateCoverageInfo({
                  coveragePriority: Number(
                    selected?.value,
                  ) as CoverageInfoValue['coveragePriority'],
                })
              }
              options={priorityOptions}
              value={getSelectedUniversalSelectOption(
                coveragePriority?.toString(),
                priorityOptions,
              )}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__start-date`}
            isRequired={false}
            label="Start date"
          >
            <DatePickerInput
              defaultDate={
                startDate ? DateTime.fromISO(startDate).toJSDate() : null
              }
              id={`${TOP_LEVEL_ID}__start-date`}
              maxDate={endDate ? new Date(endDate) : null}
              onSubmit={(date: Date) =>
                updateCoverageInfo({
                  startDate: DateTime.fromJSDate(date).toISODate(),
                })
              }
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__end-date`}
            isRequired={false}
            label="End date"
          >
            <DatePickerInput
              defaultDate={
                endDate ? DateTime.fromISO(endDate).toJSDate() : null
              }
              id={`${TOP_LEVEL_ID}__end-date`}
              minDate={new Date(startDate)}
              onSubmit={(date: Date) => {
                updateCoverageInfo({
                  endDate: DateTime.fromJSDate(date).toISODate(),
                });
              }}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__provider-services-phone-number`}
            isRequired={false}
            label="Provider services phone number"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__provider-services-phone-number`}
              leftIcon={FAIconName.phoneRegular}
              onChange={updateStringValue('providerServicesPhoneNumber')}
              type={TextInputV2Type.PHONE}
              value={providerServicesPhoneNumber}
            />
          </InputGroupV2>
        </ChakraSimpleGrid>
      </InquiryDrawerLayout>
    );
  },
);

export default CoverageInfo;
